import { gql } from "lib/apolloClient";
import {
  gradeScaleFragment,
  plannerFragment,
  plannerElementNodeSetFragment,
} from "./StandardsSelectionModalFragments";

// to fetch standard sets from either curriculum program or learning course
export const getStandardSetsQuery = gql`
  query getStandardSets(
    $id: ID!
    $type: ENTITY_TYPE_ENUM!
    $plannerElementNodeSetFilters: PlannerElementNodeSetFilters
    $plannerElementNodeFilters: PlannerElementNodeFilters
    $isPYPCurriculum: Boolean! = false
    $orderBy: OrderByEnum
    $orderByDirection: OrderByDirectionEnum
  ) {
    node(id: $id, type: $type) {
      id
      ... on CurriculumProgram {
        id
        plannerElementNodeSets(
          orderBy: $orderBy
          orderByDirection: $orderByDirection
          filters: $plannerElementNodeSetFilters
        ) {
          edges {
            node {
              ...plannerElementNodeSetItem
            }
          }
        }
      }
      ... on LearningCourse {
        id
        standardsWrapper(
          orderByDirection: $orderByDirection
          filters: $plannerElementNodeFilters
        ) @skip(if: $isPYPCurriculum) {
          standardsEdge {
            standardSet {
              ...plannerElementNodeSetItem
            }
          }
        }

        benchmarksWrapper(
          orderByDirection: $orderByDirection
          filters: $plannerElementNodeFilters
        ) @include(if: $isPYPCurriculum) {
          standardsEdge {
            standardSet {
              ...plannerElementNodeSetItem
            }
          }
        }
      }
    }
  }
  ${plannerElementNodeSetFragment}
`;

// to fetch all standards from a given standard set
export const getPublishedSubjectStandardDetailsQuery = gql`
  query getPublishedStandardSetDetails(
    $id: ID!
    $filters: PlannerElementNodeFilters
    $isPYPCurriculum: Boolean! = false
  ) {
    node(id: $id, type: PLANNER_ELEMENT_NODE_SET) {
      ... on PlannerElementNodeSet {
        id
        standardSetWrapper {
          publishedStandards(filters: $filters) @skip(if: $isPYPCurriculum) {
            edges {
              node {
                ...plannerElementNodeItem
              }
            }
          }

          publishedBenchmarks(filters: $filters)
            @include(if: $isPYPCurriculum) {
            edges {
              node {
                ...plannerBenchmarkItem
              }
            }
          }
        }
      }
    }
  }
  ${plannerFragment.plannerElementNodeFragment}
  ${plannerFragment.plannerBenchmark}
`;

// to fetch course standards from a given standard set
export const getLearningCourseStandardsQuery = gql`
  query getLearningCourseStandards(
    $id: ID!
    $filters: PlannerElementNodeFilters
    $isPYPCurriculum: Boolean! = false
  ) {
    node(id: $id, type: LEARNING_COURSE) {
      id
      ... on LearningCourse {
        standardsWrapper(filters: $filters) @skip(if: $isPYPCurriculum) {
          standardsEdge {
            standards {
              ...plannerElementNodeItem
            }
          }
        }

        benchmarksWrapper(filters: $filters) @include(if: $isPYPCurriculum) {
          standardsEdge {
            standards {
              ...plannerBenchmarkItem
            }
          }
        }
      }
    }
  }
  ${plannerFragment.plannerElementNodeFragment}
  ${plannerFragment.plannerBenchmark}
`;

// to fetch subjects, grades and tags for a given organization
// this is used in the standards selection screen to filter the standards
export const getOrganizationSubjectsGradesAndTagsQuery = gql`
  query getOrganizationSubjectsGradesAndTags(
    $orgId: ID!
    $curriculumProgramId: ID!
    $genericTagsFilter: GenericTagsFilter
  ) {
    node(id: $orgId, type: ORGANIZATION) {
      ... on Organization {
        id
        subjects(
          filters: {
            curriculumProgramIds: [$curriculumProgramId]
            archivalState: ACTIVE
          }
        ) {
          id
          name
        }
        grades(filters: { curriculumProgramIds: [$curriculumProgramId] }) {
          id
          name
        }
        genericTagsV2(filters: $genericTagsFilter) {
          id
          label
        }
      }
    }
  }
`;

// to fetch grade scales from either curriculum program
// this is used in the standard set selection screen to filter the standards
export const getGradeScalesQuery = gql`
  query getGradeScales(
    $id: ID!
    $type: ENTITY_TYPE_ENUM!
    $filters: GradeScaleFilters
  ) {
    node(id: $id, type: $type) {
      ... on CurriculumProgram {
        id
        gradeScale(filters: $filters) {
          ...gradeScaleItem
        }
      }
    }
  }
  ${gradeScaleFragment}
`;
